import React, { useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { SerialNumberDuplicateCheck } from "../Pages/General/DeviceForm";
import { useMutation } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios";
import useDialogStore from "../stores/useDialogStore";

const EditSerialNumberCell = ({
  value,
  id,
  field = "serial_no",
  onSave,
  inputClassName = "w-full px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-900 text-center",
  displayClassName = "block w-full px-2 py-1 text-sm font-medium text-gray-900 text-left",
  maxLength = 10,
  type = "text",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [error, setError] = useState(null);
  const { openDialog, closeDialog } = useDialogStore();
  const { mutateAsync: checkSerialNumber, isPending: isChecking } = useMutation(
    {
      mutationFn: async (serial) => {
        const response = await axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/devices/check-serial-number?serial_no=${serial}`
        );
        return response.data;
      },
      onError: () => {
        setError("Failed to check serial number. Please try again.");
      },
    }
  );

  const handleSave = async () => {
    setError(null);
    if (inputValue === value) {
      setIsEditing(false);
      return;
    }
    try {
      const result = await checkSerialNumber(inputValue);
      if (result.exists) {
        openDialog({
          title: "Serial Number Duplicate Check",
          content: (
            <SerialNumberDuplicateCheck
              onClose={closeDialog}
              onSubmit={() => {
                onSave({ id, field, value: inputValue });
                closeDialog();
                setIsEditing(false);
              }}
              messages={result?.messages}
            />
          ),
        });
      } else {
        onSave({ id, field, value: inputValue });
        setIsEditing(false);
      }
    } catch (e) {
      setError("Failed to check serial number. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      setIsEditing(false);
      setInputValue(value || "");
    }
  };

  const formatDisplayValue = (val) => {
    if (!val) return "————";
    const strVal = val.toString();
    return strVal;
  };

  return (
    <div className="flex items-center space-x-2 cursor-pointer">
      <div onDoubleClick={() => setIsEditing(true)} className="relative">
        {isEditing ? (
          <input
            type={type}
            className={inputClassName}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            style={{ width: "100%", minWidth: "120px" }}
            disabled={isChecking}
          />
        ) : (
          <div className="relative group">
            <span className={displayClassName} title={value}>
              {formatDisplayValue(value)}
            </span>
            <div className="absolute -top-1 -right-1 w-0.5 h-0.5 bg-blue-500 rounded-full"></div>
            <div className="absolute shadow-xl left-1/2 transform -translate-x-1/2 -translate-y-full bg-white text-gray-700 text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              {value}
              <span className="text-xs text-gray-500 block mt-1">
                double click to edit
              </span>
            </div>
          </div>
        )}
      </div>
      {isEditing && (
        <button
          onClick={handleSave}
          className="p-1 text-gray-400 hover:text-gray-600"
          disabled={isChecking}
        >
          <CheckIcon className="h-5 w-5" />
        </button>
      )}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
};

export default EditSerialNumberCell;
